
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Eye, Target, TrendingUp, Play, Pause, Loader2 } from 'lucide-react';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { CustomsGate } from '../../nobel';
import { contentPreloader } from '../../../services/contentPreloader';
import { gamificationService } from '../../../services/gamificationService';

export const TimeWarpReading: React.FC = () => {
  const userId = sessionStorage.getItem('ielts_user_id') || '';
  const { canAccess, tier, isLoading } = useFeatureAccess('advanced_module', userId);
  const [paywallOpen, setPaywallOpen] = useState(false);

  const [wpm, setWpm] = useState(300);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [peripheralMode, setPeripheralMode] = useState(false);
  const [words, setWords] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Load content
  useEffect(() => {
    if (isLoading) return;

    if (!canAccess) {
      setPaywallOpen(true);
      return;
    }

    const loadContent = async () => {
      setLoading(true);
      try {
        const content = await contentPreloader.getInstantContent('reading');
        if (content && content.paragraphs) {
          const text = content.paragraphs.join(' ');
          setWords(text.split(/\s+/));
        } else {
          // Fallback text
          setWords("The IELTS Reading test is designed to assess a wide range of reading skills, including reading for gist, reading for main ideas, reading for detail, skimming, understanding logical argument and recognizing writers' opinions, attitudes and purpose.".split(' '));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadContent();
  }, [canAccess, isLoading]);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentWordIndex(prev => {
        if (prev >= words.length - 1) {
          setIsPlaying(false);
          // Award XP on completion
          if (userId) gamificationService.awardXP(userId, 'reading_complete');
          return 0;
        }
        return prev + 1;
      });

      // Haptic feedback on mobile
      if ('vibrate' in navigator && currentWordIndex % 10 === 0) {
        navigator.vibrate(10);
      }
    }, 60000 / wpm);

    return () => clearInterval(interval);
  }, [isPlaying, wpm, currentWordIndex, words.length]);

  // Optimal Recognition Point calculation
  const getORP = (word: string) => {
    if (!word) return { before: '', orp: '', after: '' };
    const len = word.length;
    const orpIndex = len <= 1 ? 0 : len <= 5 ? 1 : len <= 9 ? 2 : 3;

    return {
      before: word.substring(0, orpIndex),
      orp: word.charAt(orpIndex),
      after: word.substring(orpIndex + 1)
    };
  };

  const currentWord = words[currentWordIndex] || "READY";
  const { before, orp, after } = getORP(currentWord);

  return (
    <>
      <div className="h-full bg-gradient-to-br from-[#0a0a0a] to-[#1c1917] rounded-3xl overflow-hidden relative flex flex-col">
        {/* Breathing Background Animation */}
        <motion.div
          className="absolute inset-0 opacity-10 pointer-events-none"
          animate={{
            scale: [1, 1.02, 1],
            opacity: [0.1, 0.15, 0.1]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            background: 'radial-gradient(circle at 50% 50%, #C5A059 0%, transparent 70%)'
          }}
        />

        {/* Header Controls */}
        <div className="relative z-10 p-8 flex justify-between items-center border-b border-white/10">
          <div>
            <h2 className="text-3xl font-black text-white mb-2 tracking-tight">
              TIME WARP READING
            </h2>
            <p className="text-stone-400 text-sm mono">
              Neural Speed Training System
            </p>
          </div>

          <motion.div
            className="flex items-center gap-4 bg-white/5 px-6 py-3 rounded-xl border border-white/10"
            whileHover={{ scale: 1.05 }}
          >
            <Zap className="text-[#C5A059]" size={24} />
            <div>
              <div className="text-xs mono text-stone-500 uppercase">Flow Rate</div>
              <div className="text-2xl font-black text-[#C5A059]">{wpm} WPM</div>
            </div>
          </motion.div>
        </div>

        {/* Main Reading Area */}
        <div className="relative z-10 flex-1 flex items-center justify-center p-16">
          {loading ? (
            <Loader2 className="animate-spin text-[#C5A059]" size={48} />
          ) : (
            <div className="relative">
              {/* Peripheral Vision Expanding Circles (when active) */}
              <AnimatePresence>
                {peripheralMode && (
                  <>
                    {[1, 2, 3].map((i) => (
                      <motion.div
                        key={i}
                        className="absolute inset-0 border-2 border-[#C5A059]/20 rounded-full"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{
                          scale: [1, 1.5, 2][i - 1],
                          opacity: [0.3, 0.2, 0][i - 1]
                        }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.5
                        }}
                      />
                    ))}
                  </>
                )}
              </AnimatePresence>

              {/* Word Display with ORP */}
              <motion.div
                key={currentWordIndex}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.1 }}
                className="relative"
              >
                {/* Alignment Guide Lines */}
                <div className="absolute left-1/2 -translate-x-1/2 -top-16 w-1 h-12 bg-[#C5A059]/30" />
                <div className="absolute left-1/2 -translate-x-1/2 -bottom-16 w-1 h-12 bg-[#C5A059]/30" />

                <div className="text-7xl md:text-8xl font-bold mono flex items-center justify-center whitespace-nowrap">
                  <span className="text-stone-600 text-right">{before}</span>
                  <motion.span
                    className="text-[#C5A059] mx-1"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 0.3, repeat: Infinity }}
                  >
                    {orp}
                  </motion.span>
                  <span className="text-stone-400 text-left">{after}</span>
                </div>
              </motion.div>
            </div>
          )}
        </div>

        {/* Bottom Controls */}
        <div className="relative z-10 p-8 border-t border-white/10 bg-[#0a0a0a]/50 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Play/Pause */}
            <div className="flex justify-center gap-6">
              <motion.button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-20 h-20 rounded-full bg-[#C5A059] flex items-center justify-center shadow-2xl shadow-[#C5A059]/50"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {isPlaying ? (
                  <Pause size={32} className="text-stone-900" />
                ) : (
                  <Play size={32} className="text-stone-900 ml-1" />
                )}
              </motion.button>

              <motion.button
                onClick={() => setPeripheralMode(!peripheralMode)}
                className={`px-6 py-3 rounded-xl font-bold mono text-sm transition-all border flex items-center ${peripheralMode
                    ? 'bg-[#C5A059] text-stone-900 border-[#C5A059]'
                    : 'bg-white/5 text-stone-400 border-white/10'
                  }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Eye size={20} className="mr-2" />
                PERIPHERAL MODE
              </motion.button>
            </div>

            {/* WPM Slider */}
            <div className="space-y-3">
              <div className="flex justify-between text-sm mono text-stone-500">
                <span>BEGINNER (100)</span>
                <span>EXPERT (1000)</span>
              </div>

              <div className="relative">
                <input
                  type="range"
                  min="100"
                  max="1000"
                  step="50"
                  value={wpm}
                  onChange={(e) => setWpm(parseInt(e.target.value))}
                  className="w-full h-2 bg-stone-800 rounded-full appearance-none cursor-pointer accent-[#C5A059]"
                  style={{
                    background: `linear-gradient(to right, #C5A059 0%, #C5A059 ${((wpm - 100) / 900) * 100}%, #292524 ${((wpm - 100) / 900) * 100}%, #292524 100%)`
                  }}
                />

                {/* Speed Zone Indicators */}
                <div className="absolute top-full mt-2 w-full flex justify-between text-xs mono">
                  <span className={wpm < 300 ? 'text-[#C5A059] font-bold' : 'text-stone-600'}>
                    WARM-UP
                  </span>
                  <span className={wpm >= 300 && wpm < 600 ? 'text-[#C5A059] font-bold' : 'text-stone-600'}>
                    OPTIMAL
                  </span>
                  <span className={wpm >= 600 ? 'text-[#C5A059] font-bold' : 'text-stone-600'}>
                    OVERDRIVE
                  </span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mt-6">
              {[
                { label: 'Words Read', value: currentWordIndex, icon: Target },
                { label: 'Session Time', value: `${Math.floor((currentWordIndex / wpm) * 10) / 10}m`, icon: Zap },
                { label: 'Progress', value: `${Math.round((currentWordIndex / words.length) * 100)}%`, icon: Eye },
                { label: 'Target WPM', value: wpm, icon: TrendingUp }
              ].map((stat) => (
                <motion.div
                  key={stat.label}
                  className="bg-white/5 rounded-xl p-4 border border-white/10"
                  whileHover={{
                    y: -4,
                    boxShadow: '0 20px 40px rgba(197, 160, 89, 0.1)'
                  }}
                >
                  <stat.icon size={20} className="text-[#C5A059] mb-2" />
                  <div className="text-xl md:text-2xl font-black text-white mb-1">{stat.value}</div>
                  <div className="text-[10px] mono text-stone-500 uppercase">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PaywallModal
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        feature="Time Warp Reading"
        reason="premium_feature"
        currentTier={tier}
      />
    </>
  );
};

export default TimeWarpReading;
